import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LivePreviewPanel = ({ 
  widgetSize, 
  bubbleIcon, 
  bubbleColor, 
  botAvatar, 
  userAvatar,
  fontSize,
  fontFamily,
  colorScheme,
  showTagline,
  taglineText,
  onlineMessage,
  offlineMessage,
  enableTyping,
  typingSpeed,
  showTypingIndicator
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const initialMessages = [
    {
      id: 1,
      type: 'bot',
      content: 'Hallo! Wie kann ich Ihnen heute helfen?',
      timestamp: new Date()
    }
  ];

  useEffect(() => {
    setMessages(initialMessages);
  }, []);

  const getFontFamilyCSS = (family) => {
    const fontMap = {
      'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'inter': '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      'roboto': '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
      'open-sans': '"Open Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      'lato': '"Lato", -apple-system, BlinkMacSystemFont, sans-serif',
      'poppins': '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif',
      'nunito': '"Nunito", -apple-system, BlinkMacSystemFont, sans-serif'
    };
    return fontMap?.[family] || fontMap?.system;
  };

  const getWidgetDimensions = () => {
    const dimensions = {
      small: { width: '320px', height: '400px' },
      medium: { width: '380px', height: '500px' },
      large: { width: '450px', height: '600px' }
    };
    return dimensions?.[widgetSize] || dimensions?.medium;
  };

  const getTypingDelay = () => {
    const delays = {
      slow: 2500,
      medium: 1500,
      fast: 800,
      instant: 0
    };
    return delays?.[typingSpeed] || delays?.medium;
  };

  const handleSendMessage = () => {
    if (!inputValue?.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Simulate bot response
    if (enableTyping && showTypingIndicator) {
      setIsTyping(true);
    }

    setTimeout(() => {
      setIsTyping(false);
      const botResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Vielen Dank für Ihre Nachricht! Ich helfe Ihnen gerne weiter.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, getTypingDelay());
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleSendMessage();
    }
  };

  const dimensions = getWidgetDimensions();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Live-Vorschau</h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsOnline(!isOnline)}
            iconName={isOnline ? "Circle" : "CircleOff"}
            iconPosition="left"
            className={isOnline ? "text-success" : "text-muted-foreground"}
          >
            {isOnline ? 'Online' : 'Offline'}
          </Button>
          <span className="text-xs text-muted-foreground">
            {dimensions?.width} × {dimensions?.height}
          </span>
        </div>
      </div>
      {/* Preview Container */}
      <div className="relative">
        {/* Chat Bubble */}
        {!isOpen && (
          <div className="fixed bottom-6 right-6 z-50">
            <button
              onClick={() => setIsOpen(true)}
              className="w-14 h-14 rounded-full shadow-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              style={{ backgroundColor: bubbleColor }}
            >
              <Icon name={bubbleIcon} size={28} color="white" />
            </button>
          </div>
        )}

        {/* Chat Widget */}
        {isOpen && (
          <div 
            className="fixed bottom-6 right-6 z-50 bg-card border border-border rounded-lg shadow-xl overflow-hidden"
            style={{ 
              width: dimensions?.width, 
              height: dimensions?.height,
              fontFamily: getFontFamilyCSS(fontFamily)
            }}
          >
            {/* Header */}
            <div 
              className="px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: colorScheme?.primary }}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20">
                  {botAvatar ? (
                    <Image src={botAvatar} alt="Bot" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-white/20 flex items-center justify-center">
                      <Icon name="Bot" size={16} color="white" />
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-white" style={{ fontSize: `${parseInt(fontSize) + 2}px` }}>
                    Chat-Support
                  </h4>
                  {showTagline && taglineText && (
                    <p className="text-white/80" style={{ fontSize: `${parseInt(fontSize) - 1}px` }}>
                      {taglineText}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Status Bar */}
            <div 
              className="px-4 py-2 border-b"
              style={{ 
                backgroundColor: isOnline ? colorScheme?.primary : colorScheme?.messageBackground,
                borderColor: colorScheme?.border
              }}
            >
              <div className="flex items-center space-x-2">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: isOnline ? '#10b981' : '#6b7280' }}
                />
                <span 
                  className="text-sm"
                  style={{ 
                    color: isOnline ? 'white' : colorScheme?.textSecondary,
                    fontSize: `${parseInt(fontSize) - 1}px`
                  }}
                >
                  {isOnline ? onlineMessage : offlineMessage}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div 
              className="flex-1 p-4 overflow-y-auto"
              style={{ 
                backgroundColor: colorScheme?.chatBackground,
                height: `calc(${dimensions?.height} - 140px)`
              }}
            >
              <div className="space-y-3">
                {messages?.map((message) => (
                  <div 
                    key={message?.id}
                    className={`flex items-start space-x-2 ${message?.type === 'user' ? 'justify-end' : ''}`}
                  >
                    {message?.type === 'bot' && (
                      <div className="w-6 h-6 rounded-full overflow-hidden border border-border flex-shrink-0">
                        {botAvatar ? (
                          <Image src={botAvatar} alt="Bot" className="w-full h-full object-cover" />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ backgroundColor: colorScheme?.primary }}
                          >
                            <Icon name="Bot" size={12} color="white" />
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div 
                      className="rounded-lg px-3 py-2 max-w-xs"
                      style={{
                        backgroundColor: message?.type === 'bot' 
                          ? colorScheme?.messageBackground 
                          : colorScheme?.userMessageBackground,
                        color: message?.type === 'bot' 
                          ? colorScheme?.textPrimary 
                          : 'white'
                      }}
                    >
                      <p style={{ fontSize: `${fontSize}px` }}>
                        {message?.content}
                      </p>
                    </div>

                    {message?.type === 'user' && (
                      <div className="w-6 h-6 rounded-full overflow-hidden border border-border flex-shrink-0">
                        {userAvatar ? (
                          <Image src={userAvatar} alt="User" className="w-full h-full object-cover" />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ backgroundColor: colorScheme?.border }}
                          >
                            <Icon name="User" size={12} style={{ color: colorScheme?.textMuted }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 rounded-full overflow-hidden border border-border flex-shrink-0">
                      {botAvatar ? (
                        <Image src={botAvatar} alt="Bot" className="w-full h-full object-cover" />
                      ) : (
                        <div 
                          className="w-full h-full flex items-center justify-center"
                          style={{ backgroundColor: colorScheme?.primary }}
                        >
                          <Icon name="Bot" size={12} color="white" />
                        </div>
                      )}
                    </div>
                    <div 
                      className="rounded-lg px-3 py-2"
                      style={{ backgroundColor: colorScheme?.messageBackground }}
                    >
                      <div className="flex items-center space-x-1">
                        <div className="flex space-x-1">
                          <div 
                            className="w-2 h-2 rounded-full animate-bounce"
                            style={{ backgroundColor: colorScheme?.textMuted }}
                          />
                          <div 
                            className="w-2 h-2 rounded-full animate-bounce"
                            style={{ 
                              backgroundColor: colorScheme?.textMuted,
                              animationDelay: '0.1s'
                            }}
                          />
                          <div 
                            className="w-2 h-2 rounded-full animate-bounce"
                            style={{ 
                              backgroundColor: colorScheme?.textMuted,
                              animationDelay: '0.2s'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Input */}
            <div 
              className="p-4 border-t"
              style={{ borderColor: colorScheme?.border }}
            >
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Nachricht eingeben..."
                  className="flex-1 px-3 py-2 border rounded-md outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  style={{
                    backgroundColor: colorScheme?.chatBackground,
                    borderColor: colorScheme?.border,
                    color: colorScheme?.textPrimary,
                    fontSize: `${fontSize}px`
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue?.trim()}
                  className="p-2 rounded-md transition-colors disabled:opacity-50"
                  style={{ backgroundColor: colorScheme?.primary }}
                >
                  <Icon name="Send" size={16} color="white" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Preview Controls */}
      <div className="flex items-center justify-center space-x-2 pt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          iconName={isOpen ? "Minimize2" : "Maximize2"}
          iconPosition="left"
        >
          {isOpen ? 'Widget schließen' : 'Widget öffnen'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMessages(initialMessages)}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Chat zurücksetzen
        </Button>
      </div>
    </div>
  );
};

export default LivePreviewPanel;